import {
  IconArrowLeft,
  IconBuildingBank,
  IconBulb,
  IconCheck,
  IconFlag,
  IconGift,
  IconPlayerPlay,
  IconProgress,
} from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { getClientById, getContractById } from "../../content";
import { analytics } from "../../analytics/analyticsEvents";
import { useLocalize } from "../../content/useLocalize";
import { DEFAULT_CLIENT_TRUST, baseRewardForDifficulty } from "../../engine/progression";
import { rewardRange, taskScoreBand, type ScoreBand } from "../../engine/scoring";
import { contractTypeText } from "../../i18n/enumText";
import { taskHintText } from "../../i18n/taskHints";
import { taskScreenFor } from "../../app/taskScreen";
import { isUrgentActive } from "../../engine/contractOffers";
import { useGameStore } from "../../state/store";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { Panel } from "../common/Panel";
import { ProgressBar } from "../common/ProgressBar";
import { StatTile } from "../common/StatTile";
import { cx } from "../common/cx";
import { formatMoney, formatNumber } from "../common/format";
import { ContractRiskBadge } from "../contracts/ContractRiskBadge";
import { RewardGradeHint } from "../contracts/RewardGradeHint";
import { UrgentBanner } from "../contracts/UrgentBanner";
import styles from "./Workspace.module.css";

const SEGMENT_BAND_CLASS: Record<ScoreBand, string | undefined> = {
  good: styles.segGood,
  partial: styles.segPartial,
  fail: styles.segFail,
};

export function Workspace(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const activeContractId = useGameStore((state) => state.activeContractId);
  const activeTaskIndex = useGameStore((state) => state.activeTaskIndex);
  const currentTaskResults = useGameStore((state) => state.currentTaskResults);
  const clientTrust = useGameStore((state) => state.clientTrust);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const completeContract = useGameStore((state) => state.completeContract);
  const offers = useGameStore((state) => state.offers);
  const refreshOffersNow = useGameStore((state) => state.refreshOffersNow);

  const contract = activeContractId ? getContractById(activeContractId) : undefined;

  if (!contract) {
    return (
      <EmptyState
        title={<Trans>Контракт не выбран</Trans>}
        action={
          <Button variant="secondary" onClick={() => navigateTo("contracts")}>
            <Trans>К контрактам</Trans>
          </Button>
        }
      />
    );
  }

  const client = getClientById(contract.clientId);
  const total = contract.tasks.length;
  const done = Math.min(activeTaskIndex, total);
  const allDone = activeTaskIndex >= total;
  const currentStep = allDone ? total : activeTaskIndex + 1;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const currentTask = allDone ? undefined : contract.tasks[activeTaskIndex];
  const urgent =
    offers.urgent && isUrgentActive(offers, contract.id, Date.now()) ? offers.urgent : null;

  const reward = contract.reward ?? baseRewardForDifficulty(contract.difficulty);
  const moneyR = rewardRange(reward.money);
  const xpR = rewardRange(reward.xp);
  const repR = rewardRange(reward.reputation);
  const trust = clientTrust[contract.clientId] ?? client?.trust ?? DEFAULT_CLIENT_TRUST;

  const exitProject = (): void => {
    const done = currentTaskResults.length;
    if (done > 0 && done < contract.tasks.length) {
      analytics.contractAbandoned({
        contractId: contract.id,
        clientId: contract.clientId,
        progressPct: Math.round((done / contract.tasks.length) * 100),
        lastScreen: "workspace",
      });
    }
    navigateTo("contracts");
  };

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <button type="button" className={styles.back} onClick={exitProject}>
          <IconArrowLeft size={16} aria-hidden="true" />
          <Trans>Выйти из проекта</Trans>
        </button>

        <Panel
          title={t(contract.title)}
          actions={
            <div className={styles.headerMeta}>
              {contract.riskLevel && <ContractRiskBadge riskLevel={contract.riskLevel} />}
              {client && <span className={styles.client}>{t(client.name)}</span>}
            </div>
          }
        >
          {urgent && (
            <UrgentBanner
              deadlineMs={urgent.deadlineMs}
              durationMs={urgent.durationMs}
              bonusPct={urgent.bonusPct}
              onExpire={refreshOffersNow}
            />
          )}

          <ol className={styles.tasks}>
            {contract.tasks.map((task, index) => {
              const taskDone = index < activeTaskIndex;
              const current = index === activeTaskIndex;
              const score = currentTaskResults[index];
              return (
                <li
                  key={`${task}-${index}`}
                  className={cx(styles.task, taskDone && styles.done, current && styles.current)}
                >
                  <span className={styles.marker} aria-hidden="true">
                    {taskDone ? <IconCheck size={16} /> : index + 1}
                  </span>
                  <span className={styles.taskName}>{i18n._(contractTypeText(task))}</span>
                  {taskDone && score !== undefined && (
                    <span className={cx(styles.score, "mono")}>{score}</span>
                  )}
                  {current && (
                    <span data-tutorial="task-action">
                      <Button
                        variant="primary"
                        size="small"
                        icon={<IconPlayerPlay size={16} />}
                        onClick={() => navigateTo(taskScreenFor(task))}
                      >
                        <Trans>Начать</Trans>
                      </Button>
                    </span>
                  )}
                </li>
              );
            })}
          </ol>

          {allDone && (
            <span data-tutorial="complete-contract">
              <Button
                variant="primary"
                icon={<IconFlag size={17} />}
                onClick={() => completeContract()}
              >
                <Trans>Завершить контракт</Trans>
              </Button>
            </span>
          )}
        </Panel>
      </div>

      <aside className={styles.side}>
        <Panel title={<Trans>Прогресс</Trans>} icon={<IconProgress size={17} />}>
          <div className={styles.progressRow}>
            <span className={styles.progressCount}>
              <Trans>
                Задача {currentStep} из {total}
              </Trans>
            </span>
            <span className={cx(styles.progressPct, "mono")}>{percent}%</span>
          </div>
          <ProgressBar value={done} max={total} context="accent" label={i18n._(msg`Прогресс`)} />
          <div className={styles.segments} aria-hidden="true">
            {contract.tasks.map((task, index) => {
              const taskDone = index < activeTaskIndex;
              const score = currentTaskResults[index];
              const bandClass =
                taskDone && score !== undefined
                  ? SEGMENT_BAND_CLASS[taskScoreBand(score)]
                  : undefined;
              return (
                <span
                  key={`${task}-${index}`}
                  className={cx(
                    styles.segment,
                    bandClass,
                    index === activeTaskIndex && !allDone && styles.segActive,
                  )}
                />
              );
            })}
          </div>
        </Panel>

        <Panel title={<Trans>Награды</Trans>} icon={<IconGift size={17} />}>
          <div className={styles.rewards}>
            <StatTile
              label={<Trans>Деньги</Trans>}
              value={`${formatMoney(moneyR.min)} … ${formatMoney(moneyR.max)}`}
              tone="money"
            />
            <StatTile
              label={<Trans>Опыт</Trans>}
              value={`+${formatNumber(xpR.min)} … +${formatNumber(xpR.max)}`}
            />
            <StatTile
              label={<Trans>Репутация</Trans>}
              value={`+${formatNumber(repR.min)} … +${formatNumber(repR.max)}`}
            />
          </div>
          <RewardGradeHint />
        </Panel>

        {client && (
          <Panel title={<Trans>Клиент</Trans>} icon={<IconBuildingBank size={17} />}>
            <div className={styles.clientName}>{t(client.name)}</div>
            <div className={styles.trustRow}>
              <span className={styles.trustLabel}>
                <Trans>Доверие</Trans>
              </span>
              <span className={cx(styles.trustVal, "mono")}>{`${formatNumber(trust)} / 100`}</span>
            </div>
            <ProgressBar value={trust} max={100} context="info" size="mini" label={i18n._(msg`Доверие`)} />
          </Panel>
        )}

        {currentTask && (
          <div className={styles.hintBox}>
            <IconBulb size={16} aria-hidden="true" />
            <span>{i18n._(taskHintText(currentTask))}</span>
          </div>
        )}
      </aside>
    </div>
  );
}
